import { useEffect, useState } from 'react'
import { ReactNode } from 'react'
import { Tabbar } from '@systemeio/components'
import { useTranslation } from 'next-i18next/pages'
import { useRouter } from 'next/router'
import useUser, { isAuthorizedUser } from 'shared/hooks/use-user'
import { limitObj_12_24_48 } from 'shared/route-query/constants/pagination-limit-array'
import { useFilterPagination } from 'shared/route-query/hooks/use-filter-pagination'
import { baseServerSideProps } from 'shared/utils/base-server-side-props'
import { withDomainAssets } from 'shared/utils/with-domain-assets'
import { EnrollmentsTab } from 'modules/school/components/enrollments-tab'
import {
  EnrollmentsTabFilter,
  defaultFilter,
} from 'modules/school/components/enrollments-tab-filter'
import { ExploreCoursesTab } from 'modules/school/components/explore-courses-tab'
import { useEnrollments } from 'modules/school/hooks/use-enrollments'
import { getSchoolLayout } from 'modules/school/layout/school-layout'

function School(): ReactNode {
  const { t } = useTranslation()

  const [isCheckedIfShouldRedirect, setIsCheckedIfShouldRedirect] = useState(false)

  const router = useRouter()
  const { tab } = router.query

  const { user } = useUser()
  const { limit, setLimit, pagination, setPagination, filter, setFilter } = useFilterPagination({
    defaultFilter,
    isStaticUrl: true,
    initLimit: limitObj_12_24_48.short,
  })

  const isUserAuthorised = isAuthorizedUser(user)

  const { enrollments } = useEnrollments({
    limit,
    pagination,
    filter,
    afterFetch: data => {
      if (
        !isCheckedIfShouldRedirect &&
        !filter.searchBy &&
        tab === 'enrollments' &&
        data.items.length === 0
      ) {
        router.replace({
          query: { tab: 'explore_courses' },
        })
      }
      setIsCheckedIfShouldRedirect(true)
      return data
    },
    shouldFetch: tab === 'enrollments',
  })

  useEffect(() => {
    // NOTE: prevent redirection if user go to "enrollments" from the "explore_courses"
    if (tab === 'explore_courses') {
      setIsCheckedIfShouldRedirect(true)
    }
  }, [tab])

  useEffect(() => {
    if (user) {
      if (!isUserAuthorised && tab !== 'explore_courses')
        router.replace({
          query: { tab: 'explore_courses' },
        })
      else if (!tab) {
        router.replace({
          query: { tab: 'enrollments' },
        })
      }
    }
  }, [router, tab, user, isUserAuthorised])

  const handleSchoolTabChange = (value: string): void => {
    if (value === 'enrollments') {
      setFilter(prev => ({ ...prev, searchBy: '' }))
      router.push({
        query: { tab: 'enrollments' },
      })
    } else {
      router.push({
        query: { tab: 'explore_courses' },
      })
    }
  }

  return (
    <div>
      <Tabbar.Root
        value={tab === 'explore_courses' ? 'explore_courses' : 'enrollments'}
        onValueChange={handleSchoolTabChange}
      >
        <div className="mb-5 ml-[min(-20px,calc(-1*(100vw-1536px)/2-20px))] flex min-h-[75px] w-screen flex-col items-center justify-between bg-bg-primary pr-10 pl-[max(20px,calc((100vw-1536px)/2+20px))] md:flex-row lg:pr-[max(20px,calc((100vw-1536px)/2+20px))]">
          <Tabbar.List>
            <Tabbar.Tab
              value="enrollments"
              data-test-id="button-enrollments-school"
              className={!user || !isUserAuthorised ? 'hidden' : ''}
            >
              {t('school.tabs.enrollments_title')}
            </Tabbar.Tab>
            <Tabbar.Tab
              value="explore_courses"
              data-test-id="button-explore-courses-school"
              className={!user ? 'hidden' : ''}
            >
              {t('school.tabs.explore_courses_title')}
            </Tabbar.Tab>
            <Tabbar.Indicator />
          </Tabbar.List>
          {user && tab !== 'explore_courses' && (
            <EnrollmentsTabFilter
              className="hidden md:flex"
              filter={filter}
              setFilter={setFilter}
            />
          )}
        </div>
        <Tabbar.Panel value="enrollments">
          <EnrollmentsTab
            enrollments={enrollments}
            limit={limit}
            setLimit={setLimit}
            pagination={pagination}
            setPagination={setPagination}
            filterComponent={
              <EnrollmentsTabFilter
                className="flex md:hidden"
                filter={filter}
                setFilter={setFilter}
              />
            }
          />
        </Tabbar.Panel>
        <Tabbar.Panel value="explore_courses">
          <ExploreCoursesTab />
        </Tabbar.Panel>
      </Tabbar.Root>
    </div>
  )
}

export const getServerSideProps = withDomainAssets(baseServerSideProps)

School.getLayout = getSchoolLayout

export default School
